import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { adminFetchAuditLogs } from '../../store/slices/adminSlice';
import { pushToast } from '../../store/slices/toastSlice';

const targetTypes = ['', 'USER', 'PRODUCT', 'DOCUMENT', 'ORDER', 'MESSAGE'];

function AuditLogs() {
  const dispatch = useDispatch();
  const { auditLogs, auditPagination, isLoading } = useSelector(s => s.admin);
  const [filters, setFilters] = useState({ actor: '', targetType: '', action: '', from: '', to: '' });
  const [debounced, setDebounced] = useState(filters);
  const [sort, setSort] = useState({ field: 'createdAt', dir: 'desc' });

  const load = useCallback(() => {
    dispatch(adminFetchAuditLogs({ ...debounced, page: auditPagination.page }))
      .unwrap()
      .catch(err => dispatch(pushToast({ type:'error', message: err || 'Failed to load audit logs'})));
  }, [dispatch, debounced, auditPagination.page]);

  useEffect(() => { load(); }, [load]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setFilters(f => ({ ...f, [name]: value }));
  };
  // Debounce filters relevant fields (actor/action) 500ms
  useEffect(() => {
    const id = setTimeout(() => setDebounced(filters), 500);
    return () => clearTimeout(id);
  }, [filters]);

  const onSubmit = (e) => { e.preventDefault(); setDebounced(filters); };

  const sortedLogs = React.useMemo(() => {
    const arr = [...auditLogs];
    const { field, dir } = sort;
    arr.sort((a,b) => {
      let av = a[field];
      let bv = b[field];
      if (field === 'createdAt') {
        av = new Date(a.createdAt).getTime();
        bv = new Date(b.createdAt).getTime();
      } else {
        av = (av || '').toString().toLowerCase();
        bv = (bv || '').toString().toLowerCase();
      }
      if (av < bv) return dir === 'asc' ? -1 : 1;
      if (av > bv) return dir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [auditLogs, sort]);

  const headerClick = (field) => setSort(s => s.field === field ? { field, dir: s.dir === 'asc' ? 'desc':'asc' } : { field, dir:'asc' });
  const sortIndicator = (field) => sort.field === field ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : '';

  const exportCsv = () => {
    try {
      const headers = ['Time','ActorCompany','ActorEmail','Action','TargetType'];
      const rows = sortedLogs.map(l => [
        new Date(l.createdAt).toISOString(),
        l.actor?.companyName || '',
        l.actor?.email || '',
        l.action,
        l.targetType,
      ]);
      const csv = [headers.join(','), ...rows.map(r => r.map(val => '"'+String(val).replace(/"/g,'""')+'"').join(','))].join('\n');
      const blob = new Blob([csv], { type:'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      dispatch(pushToast({ type:'success', message:'CSV exported' }));
    } catch (e) {
      dispatch(pushToast({ type:'error', message:'CSV export failed' }));
    }
  };

  return (
    <div style={{ padding: '1rem' }}>
      <h2>Audit Logs</h2>
      <form onSubmit={onSubmit} style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap', marginBottom:'1rem' }}>
        <input name="actor" placeholder="Actor ID" value={filters.actor} onChange={onChange} />
        <select name="targetType" value={filters.targetType} onChange={onChange}>
          {targetTypes.map(tt => <option key={tt} value={tt}>{tt || 'All Targets'}</option>)}
        </select>
        <input name="action" placeholder="Action" value={filters.action} onChange={onChange} />
        <input name="from" type="date" value={filters.from} onChange={onChange} />
        <input name="to" type="date" value={filters.to} onChange={onChange} />
        <button type="submit">Apply</button>
      </form>

      {isLoading && <p>Loading...</p>}
      {!isLoading && auditLogs.length === 0 && <p>No logs found.</p>}
      {!isLoading && sortedLogs.length > 0 && (
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr>
              <th style={th} onClick={() => headerClick('createdAt')}>Time{sortIndicator('createdAt')}</th>
              <th style={th}>Actor</th>
              <th style={th} onClick={() => headerClick('action')}>Action{sortIndicator('action')}</th>
              <th style={th}>Target</th>
              <th style={th}>Diff</th>
            </tr>
          </thead>
          <tbody>
            {sortedLogs.map(log => (
              <tr key={log._id} style={{ borderBottom: '1px solid #ddd' }}>
                <td style={td}>{new Date(log.createdAt).toLocaleString()}</td>
                <td style={td}>{log.actor ? `${log.actor.companyName} (${log.actor.email})` : 'System'}</td>
                <td style={td}>{log.action}</td>
                <td style={td}>{log.targetType}</td>
                <td style={td}>
                  <details>
                    <summary>View</summary>
                    <pre style={{ whiteSpace:'pre-wrap', maxHeight:200, overflow:'auto', background:'#f7f7f7', padding:'4px' }}>{JSON.stringify({ before: log.before, after: log.after }, null, 2)}</pre>
                  </details>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div style={{ marginTop: '0.75rem', display:'flex', alignItems:'center', gap:'1rem' }}>
        <button type="button" disabled={auditPagination.page <= 1} onClick={() => dispatch(adminFetchAuditLogs({ ...filters, page: auditPagination.page - 1 }))}>Prev</button>{' '}
        <span>Page {auditPagination.page} / {auditPagination.totalPages}</span>{' '}
        <button type="button" disabled={auditPagination.page >= auditPagination.totalPages} onClick={() => dispatch(adminFetchAuditLogs({ ...filters, page: auditPagination.page + 1 }))}>Next</button>
        <button type="button" onClick={exportCsv}>Export CSV</button>
      </div>
    </div>
  );
}

const th = { textAlign: 'left', borderBottom: '2px solid #ccc', padding: '4px' };
const td = { padding: '4px' };

export default AuditLogs;
